import { LanguageSelect, t } from '../i18n';
import { useEffect, useState } from 'react';
import api from '../api/axios';
import { storeAdminSession } from '../auth/adminSession';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const ADMIN_LOGO_PATH = '/assets/night-poker-business-logo.png';

function BrandLogo() {
  const [failed, setFailed] = useState(false);

  return (
    <span className="auth-brand-logo">
      {!failed ? (
        <img
          src={ADMIN_LOGO_PATH}
          alt="NIGHT Poker Business"
          onError={() => setFailed(true)}
        />
      ) : (
        <span>NIGHT Poker Business</span>
      )}
    </span>
  );
}

export default function Login() {
  const [id, setId] = useState(() => {
    try {
      return localStorage.getItem('admin-login-id') || '';
    } catch {
      return '';
    }
  });
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => {
    try {
      return Boolean(localStorage.getItem('admin-login-id'));
    } catch {
      return false;
    }
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const registeredId = location.state?.registeredId || '';

  useEffect(() => {
    if (registeredId) setId(registeredId);
  }, [registeredId]);

  const handleLogin = async (event) => {
    event?.preventDefault();
    if (!id.trim() || !password || loading) return;

    setLoading(true);
    try {
      const res = await api.post('/admin/auth/login', {
        id: id.trim(),
        password,
      });
      const { token, user } = res.data;
      storeAdminSession(token, user);
      if (remember) localStorage.setItem('admin-login-id', id.trim());
      else localStorage.removeItem('admin-login-id');
      // LV1~LV4 운영 대시보드는 별도 권한 조회 없이 기본 접근한다.
      const level = String(user?.user_level || '').trim().toLowerCase();
      const isRoot = String(user?.user_code || '') === '10000000';
      const isOperationsAdmin = isRoot || ['lv2', 'lv3', 'lv4'].includes(level);
      navigate(isOperationsAdmin ? '/operations-dashboard' : '/dashboard', { replace: true });
    } catch (e) {
      const msg = e.response?.data?.message || e.message || t('서버 오류');
      alert(t('로그인 실패: ') + msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <section className="login-shell" aria-labelledby="admin-login-title">
        <aside className="login-intro" aria-label={t('관리자 로그인 안내')}>
          <div className="auth-brand-row">
            <BrandLogo />
            <div className="auth-brand-copy">
              <strong>ADMIN NETWORK</strong>
              <span>ACCOUNT CENTER</span>
            </div>
          </div>

          <div className="login-intro-copy">
            <p className="auth-eyebrow">SECURE ADMIN ACCESS</p>
            <h1>
              {t('안전한 관리 서비스')}
              <br />
              {t('접속을 시작합니다.')}
            </h1>
            <p>
              {t('관리자 계정 정보를 입력해 주세요.')}
            </p>
          </div>

          <ol className="login-intro-points">
            <li>
              <span>01</span>
              <div>
                <strong>{t('계정 입력')}</strong>
                <small>{t('발급받은 관리자 아이디를 입력합니다.')}</small>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <strong>{t('권한 확인')}</strong>
                <small>{t('계정 상태와 접근 권한을 확인합니다.')}</small>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <strong>{t('관리 도구 접속')}</strong>
                <small>{t('계정 상태와 접근 권한을 확인합니다.')}</small>
              </div>
            </li>
          </ol>

          <div className="login-security-note">
            <i aria-hidden="true" />
            <p>
              <strong>{t('보안 안내')}</strong> {t('공용 기기에서는 로그인 상태 유지를 선택하지 마세요.')}
            </p>
          </div>

          <p className="login-copyright">© 2026 NIGHT Poker Business</p>
        </aside>

        <section className="login-card">
          <div className="login-toolbar">
            <span className="login-account-note">{t('관리자 전용 로그인')}</span>
            <LanguageSelect className="auth-language-select" />
          </div>

          <div className="login-content">
            <header className="login-form-header">
              <span className="login-form-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="5" y="10" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M8 10V7.5a4 4 0 0 1 8 0V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M12 14v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <div>
                <p>WELCOME BACK</p>
                <h2 id="admin-login-title">{t('로그인')}</h2>
                <span>{t('관리자 계정 정보를 입력해 주세요.')}</span>
              </div>
            </header>

            <section className="login-access-notice">
              <div className="login-notice-heading">
                <span className="login-notice-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 3 5.5 5.6v5.3c0 4.2 2.7 7.9 6.5 9.1 3.8-1.2 6.5-4.9 6.5-9.1V5.6L12 3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                    <path d="m9.4 11.8 1.7 1.7 3.6-3.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div>
                  <p>SECURE ACCESS</p>
                  <h3>{t('관리자 전용 로그인')}</h3>
                </div>
                <span className="login-secure-badge"><i aria-hidden="true" /> {t('보호됨')}</span>
              </div>

              <div className="login-notice-body">
                <div>
                  <strong>{t('권한 확인')}</strong>
                  <p>{t('계정 상태와 접근 권한을 확인합니다.')}</p>
                </div>
                <div>
                  <strong>{t('접속 기록 보호')}</strong>
                  <p>{t('로그인 및 주요 관리 활동은 보안을 위해 기록됩니다.')}</p>
                </div>
              </div>
            </section>

            {registeredId && (
              <div className="login-registration-success" role="status">
                <span aria-hidden="true">✓</span>
                <p>{t('회원가입이 완료되었습니다. 바로 로그인할 수 있습니다.')}</p>
              </div>
            )}

            <form className="admin-login-form" onSubmit={handleLogin}>
              <label className="admin-auth-field">
                <span className="admin-auth-label">{t('아이디')} <strong>*</strong></span>
                <span className="admin-auth-input-wrap">
                  <i aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
                      <path d="M5.5 19a6.5 6.5 0 0 1 13 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                  </i>
                  <input
                    type="text"
                    autoComplete="username"
                    placeholder={t('관리자 아이디 입력')}
                    value={id}
                    onChange={(event) => setId(event.target.value)}
                    autoFocus
                    required
                  />
                </span>
                <small>{t('발급받은 관리자 계정 아이디를 입력해 주세요.')}</small>
              </label>

              <label className="admin-auth-field">
                <span className="admin-auth-label">{t('비밀번호')} <strong>*</strong></span>
                <span className="admin-auth-input-wrap">
                  <i aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <rect x="5" y="10" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
                      <path d="M8 10V7.5a4 4 0 0 1 8 0V10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                  </i>
                  <input
                    type="password"
                    autoComplete="current-password"
                    placeholder={t('비밀번호 입력')}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </span>
                <small>{t('대소문자를 구분하여 정확하게 입력해 주세요.')}</small>
              </label>

              <div className="login-form-options">
                <label className="login-remember-row">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                  />
                  <span aria-hidden="true" />
                  {t('로그인 아이디 저장')}
                </label>
                <span>{t('계정 문제는 상위 관리자에게 문의')}</span>
              </div>

              <button
                className="admin-login-submit"
                type="submit"
                disabled={loading || !id.trim() || !password}
              >
                {loading ? t('로그인 중…') : t('로그인')}
                <span aria-hidden="true">→</span>
              </button>
            </form>

            <p className="login-signup-prompt">
              {t('아직 계정이 없으신가요?')} <Link to="/signup">{t('회원가입')}</Link>
            </p>

            <div className="login-support-note">
              <span aria-hidden="true">!</span>
              <p>
                {t('로그인이 반복해서 실패하면 계정이 일시적으로 잠길 수 있습니다. 관리자에게 계정 상태를 확인해 주세요.')}
              </p>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
}
